import { getPageProps } from "@/utils/dataLayer";
import { z } from "zod";
import { ExerciseData } from "./exerciseSchema";

// Type definitions for TypeScript
export type StringReturnOp =
    | GetStringDataOp
    | ArrayItemOp<StringArrayReturnOp>
    | ConcatenateOp
    | JoinOp
    | IfOp<string>;

type NumberReturnOp =
    | GetNumberDataOp
    | StringLengthOp
    | ArrayLengthOp
    | ArrayItemOp<NumberArrayReturnOp>
    | IfOp<number>;

export type BooleanReturnOp =
    | GetBooleanDataOp
    | IsUserTypeOp
    | AndOp
    | OrOp
    | NotOp
    | EqualOp
    | NotEqualOp
    | GreaterThanOp
    | GreaterThanOrEqualOp
    | LessThanOp
    | LessThanOrEqualOp
    | ArrayContainsOp
    | ArrayItemOp<BooleanArrayReturnOp>
    | IfOp<boolean>;

type StringArrayReturnOp =
    | GetStringArrayDataOp
    | ArraySliceOp<string>
    | ArrayConcatOp<string>
    | IfOp<string[]>;
type NumberArrayReturnOp =
    | GetNumberArrayDataOp
    | ArraySliceOp<number>
    | ArrayConcatOp<number>
    | IfOp<number[]>;
type BooleanArrayReturnOp =
    | GetBooleanArrayDataOp
    | ArraySliceOp<boolean>
    | ArrayConcatOp<boolean>
    | IfOp<boolean[]>;

type AnyReturnOp =
    | StringReturnOp
    | NumberReturnOp
    | BooleanReturnOp
    | PrimitiveArrayReturnOp;

type Primitive = string | number | boolean;

type PrimitiveReturnOp = StringReturnOp | NumberReturnOp | BooleanReturnOp;

type PrimitiveArray =
    | (string | StringReturnOp)[]
    | (number | NumberReturnOp)[]
    | (boolean | BooleanReturnOp)[];

type PrimitiveArrayReturnOp =
    | StringArrayReturnOp
    | NumberArrayReturnOp
    | BooleanArrayReturnOp;

// Operation types
type IsUserTypeOp = ["isUserType", "client" | "coach"];
type AndOp = ["and", ...(BooleanReturnOp | boolean)[]];
type OrOp = ["or", ...(BooleanReturnOp | boolean)[]];
type NotOp = ["not", BooleanReturnOp];
type EqualOp = [
    "equal",
    PrimitiveReturnOp | Primitive,
    PrimitiveReturnOp | Primitive,
];
type NotEqualOp = [
    "notEqual",
    PrimitiveReturnOp | Primitive,
    PrimitiveReturnOp | Primitive,
];
type GreaterThanOp = [
    "greaterThan",
    NumberReturnOp | number,
    NumberReturnOp | number,
];
type GreaterThanOrEqualOp = [
    "greaterThanOrEqual",
    NumberReturnOp | number,
    NumberReturnOp | number,
];
type LessThanOp = [
    "lessThan",
    NumberReturnOp | number,
    NumberReturnOp | number,
];
type LessThanOrEqualOp = [
    "lessThanOrEqual",
    NumberReturnOp | number,
    NumberReturnOp | number,
];
type GetStringDataOp = ["getStringData", string];
type GetNumberDataOp = ["getNumberData", string];
type GetBooleanDataOp = ["getBooleanData", string];
type GetStringArrayDataOp = ["getStringArrayData", string];
type GetNumberArrayDataOp = ["getNumberArrayData", string];
type GetBooleanArrayDataOp = ["getBooleanArrayData", string];
type StringLengthOp = ["stringLength", StringReturnOp];
type ArrayLengthOp = ["arrayLength", PrimitiveArrayReturnOp];
type ArrayContainsOp = [
    "arrayContains",
    PrimitiveArrayReturnOp,
    ...(PrimitiveReturnOp | Primitive)[],
];
type ArrayItemOp<T extends PrimitiveArrayReturnOp> = [
    "arrayItem",
    T,
    NumberReturnOp | number,
];
type ConcatenateOp = ["concatenate", ...(PrimitiveReturnOp | Primitive)[]];
type JoinOp = ["join", StringReturnOp | string, StringArrayReturnOp | string[]];
type ArraySliceOp<T extends string | number | boolean> = [
    "arraySlice",
    T extends string
        ? StringArrayReturnOp | string[]
        : T extends number
          ? NumberArrayReturnOp | number[]
          : BooleanArrayReturnOp | boolean[],
    (NumberReturnOp | number | undefined)?,
    (NumberReturnOp | number | undefined)?,
];
type ArrayConcatOp<T extends string | number | boolean> = [
    "arrayConcat",
    T extends string
        ? StringArrayReturnOp | string[]
        : T extends number
          ? NumberArrayReturnOp | number[]
          : BooleanArrayReturnOp | boolean[],
    T extends string
        ? StringArrayReturnOp | string[]
        : T extends number
          ? NumberArrayReturnOp | number[]
          : BooleanArrayReturnOp | boolean[],
];
type IfOp<
    T extends string | number | boolean | string[] | number[] | boolean[],
> = [
    "if",
    BooleanReturnOp | boolean,
    T extends string
        ? StringReturnOp | string
        : T extends number
          ? NumberReturnOp | number
          : T extends boolean
            ? BooleanReturnOp | boolean
            : T extends string[]
              ? StringArrayReturnOp | string[]
              : T extends number[]
                ? NumberArrayReturnOp | number[]
                : BooleanArrayReturnOp | boolean[],
    T extends string
        ? StringReturnOp | string
        : T extends number
          ? NumberReturnOp | number
          : T extends boolean
            ? BooleanReturnOp | boolean
            : T extends string[]
              ? StringArrayReturnOp | string[]
              : T extends number[]
                ? NumberArrayReturnOp | number[]
                : BooleanArrayReturnOp | boolean[],
];

// Helper type to get the return type of operation
type OpReturnType<T> = T extends StringReturnOp
    ? string
    : T extends NumberReturnOp
      ? number
      : T extends BooleanReturnOp
        ? boolean
        : T extends StringArrayReturnOp
          ? string[]
          : T extends NumberArrayReturnOp
            ? number[]
            : T extends BooleanArrayReturnOp
              ? boolean[]
              : never;

type OpArrayReturnType<T> = T extends StringArrayReturnOp
    ? string[]
    : T extends (StringReturnOp | string)[]
      ? string[]
      : T extends NumberArrayReturnOp
        ? number[]
        : T extends (NumberReturnOp | number)[]
          ? number[]
          : T extends BooleanArrayReturnOp
            ? boolean[]
            : T extends (BooleanReturnOp | boolean)[]
              ? boolean[]
              : never;

type ResolveAnyReturnType<T> = T extends AnyReturnOp
    ? OpReturnType<T>
    : T extends PrimitiveArray | PrimitiveArrayReturnOp
      ? OpArrayReturnType<T>
      : T extends string
        ? string
        : T extends number
          ? number
          : T extends boolean
            ? boolean
            : T extends undefined
              ? undefined
              : never;

/**
 * Structural validation of ops.
 *
 * Ops were previously validated with recursive zod unions, but zod tries every union branch
 * and validates every tuple item even after a mismatch, so deeply nested ops re-validated
 * their subtrees once per candidate branch at every level — exponential in nesting depth.
 * Instead, each op is checked against a signature table in a single walk: the op name is
 * looked up directly, so validation is linear in the size of the JSON. The exported zod
 * schemas wrap this walker with z.custom so consumers are unchanged.
 */
type OpValueType =
    | "string"
    | "number"
    | "boolean"
    | "stringArray"
    | "numberArray"
    | "booleanArray";

/**
 * How a single op argument is validated:
 * - OpValueType — an op returning that type, or a literal of that type
 * - "dataPath" — a literal string path into the exercise data
 * - "userType" — the literal "client" or "coach"
 * - "stringOp" / "booleanOp" / "primitiveArrayOp" — an op only, no literal allowed
 * - "primitive" — an op returning any scalar, or a scalar literal
 * - "optionalNumber" — like "number" but may be undefined or omitted (trailing only)
 * - "sameAsReturn" — an op or literal matching the return type being validated
 * - "arrayOfReturnOp" — an op returning an array of the scalar type being validated
 */
type OpArgType =
    | OpValueType
    | "dataPath"
    | "userType"
    | "stringOp"
    | "booleanOp"
    | "primitiveArrayOp"
    | "primitive"
    | "optionalNumber"
    | "sameAsReturn"
    | "arrayOfReturnOp";

type OpSignature = {
    returns: OpValueType[];
    args: OpArgType[];
    restArgs?: OpArgType;
};

const OP_SIGNATURES: Record<AnyReturnOp[0], OpSignature> = {
    getStringData: { returns: ["string"], args: ["dataPath"] },
    getNumberData: { returns: ["number"], args: ["dataPath"] },
    getBooleanData: { returns: ["boolean"], args: ["dataPath"] },
    getStringArrayData: { returns: ["stringArray"], args: ["dataPath"] },
    getNumberArrayData: { returns: ["numberArray"], args: ["dataPath"] },
    getBooleanArrayData: { returns: ["booleanArray"], args: ["dataPath"] },
    isUserType: { returns: ["boolean"], args: ["userType"] },
    not: { returns: ["boolean"], args: ["booleanOp"] },
    and: { returns: ["boolean"], args: [], restArgs: "boolean" },
    or: { returns: ["boolean"], args: [], restArgs: "boolean" },
    equal: { returns: ["boolean"], args: ["primitive", "primitive"] },
    notEqual: { returns: ["boolean"], args: ["primitive", "primitive"] },
    greaterThan: { returns: ["boolean"], args: ["number", "number"] },
    greaterThanOrEqual: { returns: ["boolean"], args: ["number", "number"] },
    lessThan: { returns: ["boolean"], args: ["number", "number"] },
    lessThanOrEqual: { returns: ["boolean"], args: ["number", "number"] },
    stringLength: { returns: ["number"], args: ["stringOp"] },
    arrayLength: { returns: ["number"], args: ["primitiveArrayOp"] },
    arrayContains: {
        returns: ["boolean"],
        args: ["primitiveArrayOp"],
        restArgs: "primitive",
    },
    arrayItem: {
        returns: ["string", "number", "boolean"],
        args: ["arrayOfReturnOp", "number"],
    },
    concatenate: { returns: ["string"], args: [], restArgs: "primitive" },
    join: { returns: ["string"], args: ["string", "stringArray"] },
    arraySlice: {
        returns: ["stringArray", "numberArray", "booleanArray"],
        args: ["sameAsReturn", "optionalNumber", "optionalNumber"],
    },
    arrayConcat: {
        returns: ["stringArray", "numberArray", "booleanArray"],
        args: ["sameAsReturn", "sameAsReturn"],
    },
    if: {
        returns: [
            "string",
            "number",
            "boolean",
            "stringArray",
            "numberArray",
            "booleanArray",
        ],
        args: ["boolean", "sameAsReturn", "sameAsReturn"],
    },
};

const OP_SIGNATURE_MAP = new Map<string, OpSignature>(
    Object.entries(OP_SIGNATURES),
);

const SCALAR_TYPES: OpValueType[] = ["string", "number", "boolean"];
const ARRAY_TYPES: OpValueType[] = [
    "stringArray",
    "numberArray",
    "booleanArray",
];

const ARRAY_TYPE_BY_SCALAR_TYPE: Partial<Record<OpValueType, OpValueType>> = {
    string: "stringArray",
    number: "numberArray",
    boolean: "booleanArray",
};

const SCALAR_TYPE_BY_ARRAY_TYPE: Partial<Record<OpValueType, OpValueType>> = {
    stringArray: "string",
    numberArray: "number",
    booleanArray: "boolean",
};

const opValidationCache = new WeakMap<object, Map<OpValueType, boolean>>();

function isLiteralOfType(value: unknown, type: OpValueType): boolean {
    if (type === "string" || type === "number" || type === "boolean") {
        return typeof value === type;
    }
    const itemType = SCALAR_TYPE_BY_ARRAY_TYPE[type];
    return (
        Array.isArray(value) && value.every((item) => typeof item === itemType)
    );
}

export function isOpReturning(
    value: unknown,
    returnType: OpValueType,
): boolean {
    if (!Array.isArray(value) || typeof value[0] !== "string") {
        return false;
    }
    const signature = OP_SIGNATURE_MAP.get(value[0]);
    if (!signature || !signature.returns.includes(returnType)) {
        return false;
    }

    let cachedResults = opValidationCache.get(value);
    if (!cachedResults) {
        cachedResults = new Map();
        opValidationCache.set(value, cachedResults);
    }
    const cachedResult = cachedResults.get(returnType);
    if (cachedResult !== undefined) {
        return cachedResult;
    }

    const result = hasValidArgs(value.slice(1), signature, returnType);
    cachedResults.set(returnType, result);
    return result;
}

function hasValidArgs(
    args: unknown[],
    signature: OpSignature,
    returnType: OpValueType,
): boolean {
    const requiredArgCount = signature.args.filter(
        (argType) => argType !== "optionalNumber",
    ).length;
    if (args.length < requiredArgCount) {
        return false;
    }
    if (!signature.restArgs && args.length > signature.args.length) {
        return false;
    }
    return args.every((arg, index) => {
        const argType =
            index < signature.args.length
                ? signature.args[index]
                : signature.restArgs;
        return argType !== undefined && isValidArg(arg, argType, returnType);
    });
}

function isValidArg(
    value: unknown,
    argType: OpArgType,
    returnType: OpValueType,
): boolean {
    switch (argType) {
        case "dataPath":
            return typeof value === "string";
        case "userType":
            return value === "client" || value === "coach";
        case "stringOp":
            return isOpReturning(value, "string");
        case "booleanOp":
            return isOpReturning(value, "boolean");
        case "primitiveArrayOp":
            return ARRAY_TYPES.some((type) => isOpReturning(value, type));
        case "primitive":
            return (
                SCALAR_TYPES.some((type) => isLiteralOfType(value, type)) ||
                SCALAR_TYPES.some((type) => isOpReturning(value, type))
            );
        case "optionalNumber":
            return (
                value === undefined ||
                typeof value === "number" ||
                isOpReturning(value, "number")
            );
        case "sameAsReturn":
            return (
                isLiteralOfType(value, returnType) ||
                isOpReturning(value, returnType)
            );
        case "arrayOfReturnOp": {
            const arrayType = ARRAY_TYPE_BY_SCALAR_TYPE[returnType];
            return arrayType !== undefined && isOpReturning(value, arrayType);
        }
        default:
            return (
                isLiteralOfType(value, argType) || isOpReturning(value, argType)
            );
    }
}

type ZodPrimitiveType = z.ZodType<Primitive>;
type ZodAnyReturnOpType = z.ZodType<AnyReturnOp>;
type ZodStringReturnOpType = z.ZodType<StringReturnOp>;
type ZodNumberReturnOpType = z.ZodType<NumberReturnOp>;
type ZodBooleanReturnOpType = z.ZodType<BooleanReturnOp>;
type ZodPrimitiveReturnOpType = z.ZodType<PrimitiveReturnOp>;
type ZodStringArrayReturnOpType = z.ZodType<StringArrayReturnOp>;
type ZodNumberArrayReturnOpType = z.ZodType<NumberArrayReturnOp>;
type ZodBooleanArrayReturnOpType = z.ZodType<BooleanArrayReturnOp>;
type ZodPrimitiveArrayReturnOpType = z.ZodType<PrimitiveArrayReturnOp>;

export const stringReturnOpSchema: ZodStringReturnOpType =
    z.custom<StringReturnOp>((value) => isOpReturning(value, "string"), {
        message: "Invalid string operation",
    });

export const numberReturnOpSchema: ZodNumberReturnOpType =
    z.custom<NumberReturnOp>((value) => isOpReturning(value, "number"), {
        message: "Invalid number operation",
    });

export const booleanReturnOpSchema: ZodBooleanReturnOpType =
    z.custom<BooleanReturnOp>((value) => isOpReturning(value, "boolean"), {
        message: "Invalid boolean operation",
    });

export const primitiveSchema: ZodPrimitiveType = z.union([
    z.string(),
    z.number(),
    z.boolean(),
]);

export const primitiveReturnOpSchema: ZodPrimitiveReturnOpType =
    z.custom<PrimitiveReturnOp>(
        (value) => SCALAR_TYPES.some((type) => isOpReturning(value, type)),
        { message: "Invalid primitive operation" },
    );

export const stringArrayReturnOpSchema: ZodStringArrayReturnOpType =
    z.custom<StringArrayReturnOp>(
        (value) => isOpReturning(value, "stringArray"),
        { message: "Invalid string array operation" },
    );

export const numberArrayReturnOpSchema: ZodNumberArrayReturnOpType =
    z.custom<NumberArrayReturnOp>(
        (value) => isOpReturning(value, "numberArray"),
        { message: "Invalid number array operation" },
    );

export const booleanArrayReturnOpSchema: ZodBooleanArrayReturnOpType =
    z.custom<BooleanArrayReturnOp>(
        (value) => isOpReturning(value, "booleanArray"),
        { message: "Invalid boolean array operation" },
    );

export const primitiveArrayReturnOpSchema: ZodPrimitiveArrayReturnOpType =
    z.custom<PrimitiveArrayReturnOp>(
        (value) => ARRAY_TYPES.some((type) => isOpReturning(value, type)),
        { message: "Invalid array operation" },
    );

export const anyReturnOpSchema: ZodAnyReturnOpType = z.custom<AnyReturnOp>(
    (value) =>
        SCALAR_TYPES.some((type) => isOpReturning(value, type)) ||
        ARRAY_TYPES.some((type) => isOpReturning(value, type)),
    { message: 'Invalid "any" operation' },
);

// Implementation functions with proper return types
function isUserTypeFn(
    data: ExerciseData,
    op: IsUserTypeOp,
): OpReturnType<IsUserTypeOp> {
    const { user } = getPageProps();
    const [, userType] = op;
    return user?.type === userType;
}

function andFn(data: ExerciseData, op: AndOp): OpReturnType<AndOp> {
    const [, ...items] = op;
    return items.every((item) => resolve(data, item));
}

function orFn(data: ExerciseData, op: OrOp): OpReturnType<OrOp> {
    const [, ...items] = op;
    return items.some((item) => resolve(data, item));
}

function notFn(data: ExerciseData, op: NotOp): OpReturnType<NotOp> {
    const [, item] = op;
    return !resolve(data, item);
}

function equalFn(data: ExerciseData, op: EqualOp): OpReturnType<EqualOp> {
    const [, item1, item2] = op;
    return resolve(data, item1) === resolve(data, item2);
}

function notEqualFn(
    data: ExerciseData,
    op: NotEqualOp,
): OpReturnType<NotEqualOp> {
    const [, item1, item2] = op;
    return resolve(data, item1) !== resolve(data, item2);
}

function greaterThanFn(
    data: ExerciseData,
    op: GreaterThanOp,
): OpReturnType<GreaterThanOp> {
    const [, item1, item2] = op;
    return resolve(data, item1) > resolve(data, item2);
}

function greaterThanOrEqualFn(
    data: ExerciseData,
    op: GreaterThanOrEqualOp,
): OpReturnType<GreaterThanOrEqualOp> {
    const [, item1, item2] = op;
    return resolve(data, item1) >= resolve(data, item2);
}

function lessThanFn(
    data: ExerciseData,
    op: LessThanOp,
): OpReturnType<LessThanOp> {
    const [, item1, item2] = op;
    return resolve(data, item1) < resolve(data, item2);
}

function lessThanOrEqualFn(
    data: ExerciseData,
    op: LessThanOrEqualOp,
): OpReturnType<LessThanOrEqualOp> {
    const [, item1, item2] = op;
    return resolve(data, item1) <= resolve(data, item2);
}

function getDataAtPath(data: ExerciseData, path: string): unknown {
    return path
        .split(".")
        .reduce<unknown>(
            (acc, key) =>
                typeof acc === "object" && acc !== null && key in acc
                    ? acc[key as keyof typeof acc]
                    : undefined,
            data,
        );
}

function getStringDataFn(
    data: ExerciseData,
    op: GetStringDataOp,
): OpReturnType<GetStringDataOp> {
    const [, path] = op;
    return String(getDataAtPath(data, path));
}

function getNumberDataFn(
    data: ExerciseData,
    op: GetNumberDataOp,
): OpReturnType<GetNumberDataOp> {
    const [, path] = op;
    return Number(getDataAtPath(data, path));
}

function getBooleanDataFn(
    data: ExerciseData,
    op: GetBooleanDataOp,
): OpReturnType<GetBooleanDataOp> {
    const [, path] = op;
    return Boolean(getDataAtPath(data, path));
}

function getArrayDataFn(
    data: ExerciseData,
    op: GetStringArrayDataOp | GetNumberArrayDataOp | GetBooleanArrayDataOp,
): Primitive[] {
    const [, path] = op;
    const array = getDataAtPath(data, path);
    if (array === undefined) {
        // Consider unanswered as an empty array
        return [];
    }
    if (!Array.isArray(array)) {
        throw new Error(
            `Array expected at path ${path}, got ${array} of type ${typeof array}`,
        );
    }
    return array;
}

function getStringArrayDataFn(
    data: ExerciseData,
    op: GetStringArrayDataOp,
): OpReturnType<GetStringArrayDataOp> {
    return getArrayDataFn(data, op).map((item) => String(item));
}

function getNumberArrayDataFn(
    data: ExerciseData,
    op: GetNumberArrayDataOp,
): OpReturnType<GetNumberArrayDataOp> {
    return getArrayDataFn(data, op).map((item) => Number(item));
}

function getBooleanArrayDataFn(
    data: ExerciseData,
    op: GetBooleanArrayDataOp,
): OpReturnType<GetBooleanArrayDataOp> {
    return getArrayDataFn(data, op).map((item) => Boolean(item));
}

function stringLengthFn(
    data: ExerciseData,
    op: StringLengthOp,
): OpReturnType<StringLengthOp> {
    const [, path] = op;
    return String(resolve(data, path)).length;
}

function arrayLengthFn(
    data: ExerciseData,
    op: ArrayLengthOp,
): OpReturnType<ArrayLengthOp> {
    const [, arrayOp] = op;
    return resolve(data, arrayOp).length;
}

function arrayContainsFn(
    data: ExerciseData,
    op: ArrayContainsOp,
): OpReturnType<ArrayContainsOp> {
    const [, arrayOp, ...targetOps] = op;
    const array = resolve(data, arrayOp);

    return targetOps.some((targetOp) => {
        const targetValue = resolve(data, targetOp);
        return array.some((item: Primitive) => item === targetValue);
    });
}

function arrayItemFn<T extends PrimitiveArrayReturnOp>(
    data: ExerciseData,
    op: ArrayItemOp<T>,
): OpReturnType<ArrayItemOp<T>> {
    const [, arrayOp, index] = op;
    const array = resolve(data, arrayOp);
    const indexValue = resolve(data, index);
    return array[indexValue] as OpReturnType<ArrayItemOp<T>>;
}

function concatenateFn(
    data: ExerciseData,
    op: ConcatenateOp,
): OpReturnType<ConcatenateOp> {
    const [, ...items] = op;
    return items.map((item) => resolve(data, item)).join("");
}

function joinFn(data: ExerciseData, op: JoinOp): OpReturnType<JoinOp> {
    const [, separatorOp, arrayOp] = op;
    const separator = resolve(data, separatorOp);
    const array = resolve(data, arrayOp);
    return array.join(separator);
}

function arraySliceFn<T extends string | number | boolean>(
    data: ExerciseData,
    op: ArraySliceOp<T>,
): OpArrayReturnType<T> {
    const [, arrayOp, start, end] = op;
    const array = resolve(data, arrayOp) as string[] | number[] | boolean[];
    const startValue = resolve(data, start);
    const endValue = resolve(data, end);
    return array.slice(startValue, endValue) as OpArrayReturnType<T>;
}

function arrayConcatFn<T extends string | number | boolean>(
    data: ExerciseData,
    op: ArrayConcatOp<T>,
): OpArrayReturnType<T> {
    const [, arrayOp1, arrayOp2] = op;
    const array1 = resolve(data, arrayOp1) as string[] | number[] | boolean[];
    const array2 = resolve(data, arrayOp2) as string[] | number[] | boolean[];
    return array1.concat(array2) as OpArrayReturnType<T>;
}

function ifFn<
    T extends string | number | boolean | string[] | number[] | boolean[],
>(data: ExerciseData, op: IfOp<T>): ResolveAnyReturnType<T> {
    const [, conditionOp, trueScenarioOp, falseScenarioOp] = op;
    const condition = resolve(data, conditionOp);
    if (condition) {
        return resolve(
            data,
            trueScenarioOp,
        ) as unknown as ResolveAnyReturnType<T>;
    } else {
        return resolve(
            data,
            falseScenarioOp,
        ) as unknown as ResolveAnyReturnType<T>;
    }
}

const OP_MAP: Record<
    AnyReturnOp[0],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: ExerciseData, op: any) => OpReturnType<any>
> = {
    isUserType: isUserTypeFn,
    and: andFn,
    or: orFn,
    not: notFn,
    equal: equalFn,
    notEqual: notEqualFn,
    greaterThan: greaterThanFn,
    greaterThanOrEqual: greaterThanOrEqualFn,
    lessThan: lessThanFn,
    lessThanOrEqual: lessThanOrEqualFn,
    getStringData: getStringDataFn,
    getNumberData: getNumberDataFn,
    getBooleanData: getBooleanDataFn,
    getStringArrayData: getStringArrayDataFn,
    getNumberArrayData: getNumberArrayDataFn,
    getBooleanArrayData: getBooleanArrayDataFn,
    stringLength: stringLengthFn,
    arrayLength: arrayLengthFn,
    arrayContains: arrayContainsFn,
    arrayItem: arrayItemFn,
    concatenate: concatenateFn,
    join: joinFn,
    arraySlice: arraySliceFn,
    arrayConcat: arrayConcatFn,
    if: ifFn,
};

/**
 * Handles recursive resolution of operations.
 */
export function resolveOp<T extends AnyReturnOp>(
    data: ExerciseData,
    op: T,
): OpReturnType<T> {
    const opName = op[0];

    const opFn = OP_MAP[opName];

    if (!opFn) {
        throw new Error(`Unknown operation: ${opName}`);
    }

    return opFn(data, op) as OpReturnType<T>;
}

export function resolve<
    T extends
        | Primitive
        | AnyReturnOp
        | PrimitiveArray
        | PrimitiveArrayReturnOp
        | undefined,
>(data: ExerciseData, op: T): ResolveAnyReturnType<T> {
    if (
        // This is a performance optimization to avoid validating the op if it is a primitive.
        typeof op === "string" ||
        typeof op === "number" ||
        typeof op === "boolean" ||
        typeof op === "undefined"
    ) {
        return op as unknown as ResolveAnyReturnType<T>;
    }

    if (Array.isArray(op) && typeof op[0] === "string" && op[0] in OP_MAP) {
        return resolveOp(data, op as AnyReturnOp) as ResolveAnyReturnType<T>;
    }
    if (Array.isArray(op)) {
        return op.map((item) => resolve(data, item)) as ResolveAnyReturnType<T>;
    }
    return op as unknown as ResolveAnyReturnType<T>;
}
